import React from 'react';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, ProductCardV2, ProductIngredientsList, theme } from '@sg/garnish';
import { Upsell } from '@sg/shared-fe-features-menu';
import { useLineItemModifierIngredients } from '@sg/shared-fe-features-ordering';

import { useFeatureFlag } from '@order/LaunchDarkly';
import {
  useLineItemDescription,
  useLineItemIngredients,
  useLineItemName,
} from '@order/shared-hooks';

import type { PartialLineItem } from '../../../../graphql';

// ─────────────────────────────────────────────────────────────────────────────

export const UpsellLineItemCard = (props: LineItemCardProps) => {
  const { lineItem } = props;

  const { formatMessage } = useIntl();

  const { product, quantity, perItemCost } = lineItem;
  const calories = product?.calories;
  const lineItemImageUrl = product?.asset?.url;

  const lineItemName = useLineItemName(product, lineItem);
  const description = useLineItemDescription(lineItem);

  const isDetailedModificationsEnabled = useFeatureFlag(
    'CELS-5031-detailed-line-item-modifications-enabled',
  );

  const showLabels = !product.isCustom;

  const { addedIngredients: legacyAddedIngredients, removedIngredients: legacyRemovedIngredients } =
    useLineItemIngredients(lineItem);

  const { addedIngredientsText, removedIngredientsText } = useLineItemModifierIngredients({
    selectedModifiers: lineItem.selectedModifiers,
    showLabels,
    product,
  });

  const addedIngredients = isDetailedModificationsEnabled
    ? EMPTY_INGREDIENTS
    : legacyAddedIngredients;
  const removedIngredients = isDetailedModificationsEnabled
    ? EMPTY_INGREDIENTS
    : legacyRemovedIngredients;

  const hasModdedIngredients =
    addedIngredients.length > 0 ||
    removedIngredients.length > 0 ||
    Boolean(addedIngredientsText) ||
    Boolean(removedIngredientsText);

  return (
    <Upsell.LineItemContainer>
      <Upsell.LineItemImage imageUrl={lineItemImageUrl || ''} lineItemName={lineItemName} />

      <Upsell.LineItemHeader lineItemName={lineItemName} quantity={quantity || 1} />

      <ProductCardV2.ProductDetailsContainer>
        {perItemCost ? (
          <ProductCardV2.ProductDetail sizeMatch={['18']} palette="dark-kale">
            <FormattedNumber
              value={perItemCost / 100}
              // eslint-disable-next-line react/style-prop-object
              style="currency"
              currency="USD"
            />
          </ProductCardV2.ProductDetail>
        ) : null}

        {calories !== undefined && calories !== null && (
          <ProductCardV2.ProductDetail sizeMatch={['18']}>
            <FormattedMessage {...messages.caloriesText} values={{ calories }} />
          </ProductCardV2.ProductDetail>
        )}
      </ProductCardV2.ProductDetailsContainer>

      {hasModdedIngredients ? (
        <View style={styles.ingredientListWrapper}>
          {isDetailedModificationsEnabled ? (
            <View>
              {addedIngredientsText ? (
                <BodyText sizeMatch={['16']}>{addedIngredientsText}</BodyText>
              ) : null}

              {removedIngredientsText ? (
                <BodyText sizeMatch={['16']}>{removedIngredientsText}</BodyText>
              ) : null}
            </View>
          ) : (
            <ProductIngredientsList
              addedIngredientsLabel={formatMessage(messages.addedIngredientsLabel)}
              addedIngredients={addedIngredients}
              removedIngredients={removedIngredients}
              removedIngredientsLabel={formatMessage(messages.removedIngredientsLabel)}
              shouldCapitalizeFirstIngredient={false}
              sizeMatch={['16']}
            />
          )}
        </View>
      ) : null}

      <Upsell.LineItemDescription description={description} />
    </Upsell.LineItemContainer>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  caloriesText: {
    defaultMessage: '{calories} CALS',
    description: 'Upsell | Line item | Calories',
  },
  addedIngredientsLabel: {
    defaultMessage: 'Add',
    description: 'Upsell | Line item | Added ingredients label',
  },
  removedIngredientsLabel: {
    defaultMessage: 'Remove',
    description: 'Upsell | Line item | Removed ingredients label',
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────

const EMPTY_INGREDIENTS: ReadonlyArray<{ name?: string }> = [];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientListWrapper: {
    paddingTop: theme.spacing['3'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

type LineItemCardProps = {
  lineItem: PartialLineItem;
};
